import styled from '@emotion/styled';
import type { TeamTarget } from '@racemap/sdk/schema/events';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatDistance } from '@racemap/utilities/formattings/units';
import { Unit, UnitType } from '@racemap/utilities/types/units';
import { Card, Flex, Progress } from 'antd';
import type { FC } from 'react';

interface Props {
  teamTarget: TeamTarget;
  totalDistance: number;
  unitType?: UnitType;
  defaultOpen?: boolean;
}

export const TeamTargetPanel: FC<Props> = ({
  teamTarget,
  totalDistance,
  unitType = UnitType.METRIC,
}) => {
  return (
    <Container>
      <Flex vertical>
        <TargetPanelHeader teamTarget={teamTarget} totalDistance={totalDistance} />
        <Body>
          <Current>Current</Current>
          <Goal>Goal</Goal>
          <NumberCurrent>
            {formatDistance({
              value: totalDistance ?? 0,
              inputUnit: Unit.Distance.m,
              unitType,
            })}
          </NumberCurrent>
          <NumberGoal>
            {formatDistance({
              value: teamTarget.distance ?? 0,
              inputUnit: Unit.Distance.m,
              unitType,
            })}
          </NumberGoal>
        </Body>
      </Flex>
    </Container>
  );
};

function TargetPanelHeader({
  teamTarget,
  totalDistance,
}: {
  teamTarget: TeamTarget;
  totalDistance: number;
}) {
  const now = Math.floor((totalDistance / teamTarget.distance) * 100);

  return (
    <Header>
      <HeaderTitle>{teamTarget.description} </HeaderTitle>
      <Progress percent={now} status={now >= 100 ? 'success' : undefined} />
    </Header>
  );
}

const Container = styled(Card)`
  width: 300px;
  margin: auto;
  margin-bottom: 15px;

  .ant-card-body {
    padding: 0;
  }
`;

const Body = styled.div`
  padding: 10px 15px;
  background: ${RacemapColors.LightGray};
  display: grid;
  grid-template:
    [row1-start] 'cur goal' [row1-end]
    [row2-start] 'num_cur num_goal' [row2-start]
    / 1fr 1fr;
  grid-gap: 3px;
`;

const Current = styled.div`
  grid-area: cur;
  color: ${RacemapColors.DarkGray};
`;
const Goal = styled.div`
  grid-area: goal;
  color: ${RacemapColors.DarkGray};
`;
const NumberCurrent = styled.div`
  grid-area: num_cur;
  font-size: 1.2em;
  font-weight: 500;
`;
const NumberGoal = styled.div`
  grid-area: num_goal;
  display: flex;
  align-items: center;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  height: 100%;
  padding: 15px;
`;

const HeaderTitle = styled.h3`
  font-size: 1.04em;
  margin-bottom: 10px;
`;
